import {
  Check,
  CheckCircle,
  DollarSign,
  HelpCircle,
  Plus,
  X,
} from 'lucide-react';
import { type MutableRefObject, useState } from 'react';
import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { BenefitType, CoverageCategory } from '@/services/types/coverage-types';

import { CoverageFormData } from './types';

// Helper to check if a benefit type supports stacking
const isStackableBenefitType = (benefitType: BenefitType | undefined) =>
  benefitType === BenefitType.UNINSURED_MOTORIST ||
  benefitType === BenefitType.UNDER_INSURED_MOTORIST;

interface BenefitsTabProps {
  setValue: UseFormSetValue<CoverageFormData>;
  watch: UseFormWatch<CoverageFormData>;
  updateBenefit: UseFieldArrayUpdate<CoverageFormData, 'benefits'>;
  benefitFields: FieldArrayWithId<CoverageFormData, 'benefits', 'id'>[];
  appendBenefit: UseFieldArrayAppend<CoverageFormData, 'benefits'>;
  removeBenefit: UseFieldArrayRemove;
  // Ref that the parent can call before submit to flush any pending limitation
  flushRef?: MutableRefObject<(() => void) | null>;
  // Optional props for "Mark Review Complete" functionality
  coverageCategory?: CoverageCategory | null;
  onMarkReviewComplete?: () => void;
  isMarkingComplete?: boolean;
  isReviewCompleted?: boolean;
}

type BenefitOption = {
  type: BenefitType;
  label: string;
  description: string;
};

// Group benefits by category for better UX
const benefitCategories: Record<string, BenefitOption[]> = {
  'First Party Benefits': [
    {
      type: BenefitType.PERSONAL_INJURY_PROTECTION,
      label: 'Personal Injury Protection (PIP)',
      description: 'No-fault coverage for medical expenses and lost wages',
    },
    {
      type: BenefitType.MEDICAL_PAYMENTS,
      label: 'Medical Payments (MedPay)',
      description: 'Covers medical expenses regardless of fault',
    },
    {
      type: BenefitType.UNINSURED_MOTORIST,
      label: 'Uninsured Motorist (UM)',
      description: 'Protection when hit by uninsured driver',
    },
    {
      type: BenefitType.UNDER_INSURED_MOTORIST,
      label: 'Underinsured Motorist (UIM)',
      description:
        'Additional coverage when at-fault driver has insufficient limits',
    },
  ],
  'Liability Coverage': [
    {
      type: BenefitType.BODILY_INJURY_LIABILITY,
      label: 'Bodily Injury Liability',
      description: 'Covers injuries caused to others',
    },
    {
      type: BenefitType.PROPERTY_DAMAGE_LIABILITY,
      label: 'Property Damage Liability',
      description: "Covers damage to others' property",
    },
  ],
  'Property Coverage': [
    {
      type: BenefitType.COMPREHENSIVE_AUTO,
      label: 'Comprehensive',
      description: 'Covers non-collision damage (theft, weather, etc.)',
    },
    {
      type: BenefitType.COLLISION_AUTO,
      label: 'Collision',
      description: 'Covers collision damage to your vehicle',
    },
  ],
  'Other Benefits': [
    {
      type: BenefitType.OTHER,
      label: 'Other Coverage',
      description: 'Any other type of insurance benefit',
    },
  ],
};

const LIMITATION_TYPE_OPTIONS = [
  { label: 'Per person', value: 'Per person' },
  { label: 'Per accident', value: 'Per accident' },
  { label: 'Combined single limit', value: 'Combined single limit' },
  { label: 'Will not disclose', value: 'Will not disclose' },
];

const LIMIT_PRESET_VALUES = [0, 15000, 25000, 50000, 100000, 250000];
const LIMIT_AMOUNT_INCREMENT = 1000;

interface NewLimitationState {
  benefitIndex: number;
  limitationLabel: string;
  valueAmount: string;
}

const getDefaultLimitationState = (): NewLimitationState => ({
  benefitIndex: -1,
  limitationLabel: '',
  valueAmount: '',
});

const QuickAddBenefit = ({
  categoryName,
  benefits,
  appendBenefit,
}: {
  categoryName: string;
  benefits: BenefitOption[];
  appendBenefit: UseFieldArrayAppend<CoverageFormData, 'benefits'>;
}) => (
  <Card className="border-dashed border-2 border-gray-200 hover:border-gray-300 transition-colors">
    <CardHeader className="pb-3">
      <CardTitle className="text-sm font-medium text-gray-700">
        {categoryName}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="grid grid-cols-1 gap-2">
        {benefits.map((benefit) => (
          <Button
            key={benefit.type}
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              appendBenefit({
                benefit_type: benefit.type,
                limitations: null,
              })
            }
            className="justify-start h-auto p-3 text-left"
          >
            <div className="space-y-1">
              <div className="text-sm font-medium">{benefit.label}</div>
              <div className="text-xs text-gray-500">{benefit.description}</div>
            </div>
          </Button>
        ))}
      </div>
    </CardContent>
  </Card>
);

export const BenefitsTab = ({
  setValue,
  watch,
  updateBenefit,
  benefitFields,
  appendBenefit,
  removeBenefit,
  flushRef,
  coverageCategory,
  onMarkReviewComplete,
  isMarkingComplete,
  isReviewCompleted,
}: BenefitsTabProps) => {
  const [newLimitation, setNewLimitation] = useState<NewLimitationState>(
    getDefaultLimitationState,
  );
  const [limitationTypeOpen, setLimitationTypeOpen] = useState(false);

  const reviewTaskLabel =
    coverageCategory === CoverageCategory.FIRST_PARTY
      ? 'Review and Enter 1P Benefits'
      : 'Find Limits';

  const resolvedLimitationLabel = newLimitation.limitationLabel.trim();

  const resolvedAmountValue = Number(newLimitation.valueAmount);
  const resolvedAmount =
    Number.isNaN(resolvedAmountValue) || resolvedAmountValue < 0
      ? null
      : resolvedAmountValue;

  const addLimitation = (benefitIndex: number) => {
    if (
      !resolvedLimitationLabel ||
      resolvedAmount === null ||
      benefitIndex < 0
    ) {
      return;
    }

    const currentBenefit = watch(`benefits.${benefitIndex}`);
    const currentLimitations = currentBenefit.limitations || {};
    updateBenefit(benefitIndex, {
      ...currentBenefit,
      limitations: {
        ...currentLimitations,
        [resolvedLimitationLabel]: resolvedAmount,
      },
    });
    setNewLimitation(getDefaultLimitationState());
  };

  // Keep flushRef pointing to a function that commits any valid pending limitation
  if (flushRef) {
    flushRef.current = () => {
      if (
        resolvedLimitationLabel &&
        resolvedAmount !== null &&
        newLimitation.benefitIndex >= 0
      ) {
        addLimitation(newLimitation.benefitIndex);
      }
    };
  }

  const removeLimitation = (benefitIndex: number, limitationKey: string) => {
    const currentBenefit = watch(`benefits.${benefitIndex}`);
    const currentLimitations = currentBenefit.limitations || {};
    const { [limitationKey]: _, ...remaining } = currentLimitations;
    updateBenefit(benefitIndex, {
      ...currentBenefit,
      limitations: Object.keys(remaining).length > 0 ? remaining : null,
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header with guidance */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-amber-900 mb-2">
          Insurance Benefits & Coverage Limits
        </h3>
        <p className="text-sm text-amber-800">
          Add the specific types of coverage included in this policy and their
          limits. Common benefits are organized by category below.
        </p>
      </div>

      {/* Mark Review Complete Button */}
      {onMarkReviewComplete && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            {isReviewCompleted ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <DollarSign className="h-5 w-5 text-blue-600" />
            )}
            <div>
              <p className="text-sm font-medium text-blue-900">
                {reviewTaskLabel} Task
              </p>
              <p className="text-xs text-blue-700">
                {isReviewCompleted
                  ? 'This review task has been completed'
                  : "Mark this task as complete once you've reviewed and entered the benefits"}
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant={isReviewCompleted ? 'outline' : 'default'}
            size="sm"
            onClick={onMarkReviewComplete}
            disabled={isMarkingComplete || isReviewCompleted}
            className={
              isReviewCompleted ? 'text-green-600 border-green-300' : ''
            }
          >
            {isMarkingComplete ? (
              'Completing...'
            ) : isReviewCompleted ? (
              <>
                <CheckCircle className="h-4 w-4 mr-1" />
                Completed
              </>
            ) : (
              'Mark Review Complete'
            )}
          </Button>
        </div>
      )}

      {benefitFields.length === 0 ? (
        // Empty state with quick-add categories
        <div className="space-y-4">
          <div className="text-center py-6">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Add Coverage Benefits
            </h3>
            <p className="text-gray-600 mb-6">
              Select the types of coverage included in this insurance policy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(benefitCategories).map(
              ([categoryName, benefits]) => (
                <QuickAddBenefit
                  key={categoryName}
                  categoryName={categoryName}
                  benefits={benefits}
                  appendBenefit={appendBenefit}
                />
              ),
            )}
          </div>
        </div>
      ) : (
        // Benefits list with add button
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Coverage Benefits
              </h3>
              <p className="text-sm text-gray-600">
                {benefitFields.length} benefit
                {benefitFields.length !== 1 ? 's' : ''} added
              </p>
            </div>
            <Select
              onValueChange={(value) =>
                appendBenefit({
                  benefit_type: value as BenefitType,
                  limitations: null,
                })
              }
            >
              <SelectTrigger className="w-auto gap-2 border-dashed hover:bg-gray-50">
                <Plus className="h-4 w-4" />
                <SelectValue placeholder="Add Benefit" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(benefitCategories).map(
                  ([categoryName, benefits]) => (
                    <div key={categoryName}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {categoryName}
                      </div>
                      {benefits.map((benefit) => (
                        <SelectItem key={benefit.type} value={benefit.type}>
                          <div className="space-y-1">
                            <div className="font-medium">{benefit.label}</div>
                            <div className="text-xs text-gray-500">
                              {benefit.description}
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </div>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Benefits List */}
          <div className="space-y-4">
            {benefitFields.map((field, benefitIndex) => {
              const benefitType = watch(
                `benefits.${benefitIndex}.benefit_type`,
              );
              const limitations =
                watch(`benefits.${benefitIndex}.limitations`) || {};

              // Find the benefit info for display
              const benefitInfo = Object.values(benefitCategories)
                .flat()
                .find((b) => b.type === benefitType);

              return (
                <Card key={field.id} className="border-l-4 border-l-blue-500">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-base">
                          {benefitInfo?.label || 'Unknown Benefit'}
                        </CardTitle>
                        {benefitInfo?.description && (
                          <p className="text-sm text-gray-600">
                            {benefitInfo.description}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBenefit(benefitIndex)}
                        className="text-gray-500 hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Stacking Benefits Toggle for UM/UIM */}
                    {isStackableBenefitType(benefitType) && (
                      <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <Checkbox
                          id={`stacking-${benefitIndex}`}
                          checked={
                            watch(`benefits.${benefitIndex}.is_stacked`) ===
                            true
                          }
                          onCheckedChange={(checked) => {
                            const currentBenefit = watch(`benefits.${benefitIndex}`);
                            updateBenefit(benefitIndex, {
                              ...currentBenefit,
                              is_stacked:
                                checked === true
                                  ? true
                                  : checked === false
                                    ? false
                                    : null,
                            });
                          }}
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor={`stacking-${benefitIndex}`}
                            className="text-sm font-medium cursor-pointer"
                          >
                            Stacking Benefits?
                          </Label>
                          <p className="text-xs text-amber-700">
                            When enabled, coverage limits from multiple policies
                            can be combined (stacked).
                          </p>
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="h-4 w-4 text-amber-600 hover:text-amber-800" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-sm">
                                Stacked coverage allows combining UM/UIM limits
                                from multiple vehicles on the same policy, or
                                from multiple policies. Non-stacked coverage
                                only provides the limit of one vehicle/policy.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}

                    {/* Coverage Limitations */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-medium">
                          Coverage Limits
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-sm">
                                Add specific dollar limits for this coverage
                                (e.g., per person, per accident, deductible)
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>

                      {/* Existing limitations */}
                      {Object.entries(limitations).map(([key, value]) => (
                        <div
                          key={key}
                          className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="text-sm font-medium capitalize">
                              {key}
                            </div>
                            <div className="text-lg font-semibold text-green-700">
                              {formatCurrency(value as number)}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLimitation(benefitIndex, key)}
                            className="text-gray-500 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}

                      {/* Add new limitation */}
                      {newLimitation.benefitIndex === benefitIndex ? (
                        <Card className="bg-blue-50 border-blue-200">
                          <CardContent className="p-4 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs font-medium">
                                  Limitation Type
                                </Label>
                                <div className="mt-1">
                                  <Popover
                                    open={limitationTypeOpen}
                                    onOpenChange={setLimitationTypeOpen}
                                  >
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={limitationTypeOpen}
                                        className="w-full justify-between text-sm font-normal h-9"
                                      >
                                        {newLimitation.limitationLabel ||
                                          'Select or type a limitation...'}
                                        <X
                                          className={cn(
                                            'ml-2 h-4 w-4 shrink-0 opacity-50',
                                            !newLimitation.limitationLabel &&
                                              'hidden',
                                          )}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setNewLimitation((prev) => ({
                                              ...prev,
                                              limitationLabel: '',
                                            }));
                                          }}
                                        />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      className="w-[var(--radix-popover-trigger-width)] p-0"
                                      align="start"
                                    >
                                      <Command>
                                        <CommandInput
                                          placeholder="Search or type custom..."
                                          value={newLimitation.limitationLabel}
                                          onValueChange={(value) =>
                                            setNewLimitation((prev) => ({
                                              ...prev,
                                              limitationLabel: value,
                                            }))
                                          }
                                        />
                                        <CommandList>
                                          <CommandEmpty>
                                            <button
                                              type="button"
                                              className="w-full px-2 py-1.5 text-sm text-left hover:bg-accent rounded-sm"
                                              onClick={() => {
                                                setLimitationTypeOpen(false);
                                              }}
                                            >
                                              Use &quot;
                                              {newLimitation.limitationLabel}
                                              &quot;
                                            </button>
                                          </CommandEmpty>
                                          <CommandGroup heading="Presets">
                                            {LIMITATION_TYPE_OPTIONS.map(
                                              (option) => (
                                                <CommandItem
                                                  key={option.value}
                                                  value={option.value}
                                                  onSelect={(value) => {
                                                    setNewLimitation(
                                                      (prev) => ({
                                                        ...prev,
                                                        limitationLabel: value,
                                                      }),
                                                    );
                                                    setLimitationTypeOpen(
                                                      false,
                                                    );
                                                  }}
                                                >
                                                  <Check
                                                    className={cn(
                                                      'mr-2 h-4 w-4',
                                                      newLimitation.limitationLabel ===
                                                        option.value
                                                        ? 'opacity-100'
                                                        : 'opacity-0',
                                                    )}
                                                  />
                                                  {option.label}
                                                </CommandItem>
                                              ),
                                            )}
                                          </CommandGroup>
                                        </CommandList>
                                      </Command>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                                <p className="text-[11px] text-gray-500 mt-1">
                                  Choose a preset or enter your own limitation
                                  label.
                                </p>
                              </div>
                              <div>
                                <Label className="text-xs font-medium">
                                  Amount
                                </Label>
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs px-2"
                                    onClick={() =>
                                      setNewLimitation((prev) => {
                                        const currentValue = Number(
                                          prev.valueAmount || 0,
                                        );
                                        if (Number.isNaN(currentValue)) {
                                          return {
                                            ...prev,
                                            valueAmount: '',
                                          };
                                        }
                                        const nextValue = Math.max(
                                          0,
                                          currentValue - LIMIT_AMOUNT_INCREMENT,
                                        );
                                        return {
                                          ...prev,
                                          valueAmount:
                                            nextValue > 0
                                              ? String(nextValue)
                                              : '',
                                        };
                                      })
                                    }
                                  >
                                    -1k
                                  </Button>
                                  <div className="relative flex-1">
                                    <Input
                                      type="text"
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      min={0}
                                      step={LIMIT_AMOUNT_INCREMENT}
                                      placeholder="e.g., 25000"
                                      value={newLimitation.valueAmount}
                                      onChange={(e) => {
                                        const numericValue =
                                          e.target.value.replace(/[^0-9]/g, '');
                                        setNewLimitation((prev) => ({
                                          ...prev,
                                          valueAmount: numericValue,
                                        }));
                                      }}
                                      className="text-sm pr-14"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                                      USD
                                    </span>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs px-2"
                                    onClick={() =>
                                      setNewLimitation((prev) => {
                                        const current = Number(
                                          prev.valueAmount || 0,
                                        );
                                        const sanitizedCurrent = Number.isNaN(
                                          current,
                                        )
                                          ? 0
                                          : current;
                                        const nextValue =
                                          sanitizedCurrent +
                                          LIMIT_AMOUNT_INCREMENT;
                                        return {
                                          ...prev,
                                          valueAmount: String(nextValue),
                                        };
                                      })
                                    }
                                  >
                                    +1k
                                  </Button>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {LIMIT_PRESET_VALUES.map((preset) => {
                                    const isActive =
                                      Number(newLimitation.valueAmount) ===
                                      preset;
                                    return (
                                      <Button
                                        key={preset}
                                        type="button"
                                        variant={
                                          isActive ? 'default' : 'outline'
                                        }
                                        size="sm"
                                        className="h-7 text-xs px-2"
                                        onClick={() =>
                                          setNewLimitation((prev) => ({
                                            ...prev,
                                            valueAmount: String(preset),
                                          }))
                                        }
                                      >
                                        {formatCurrency(preset)}
                                      </Button>
                                    );
                                  })}
                                </div>
                                <p className="text-[11px] text-gray-500 mt-2">
                                  {resolvedAmount !== null
                                    ? `Equivalent to ${formatCurrency(resolvedAmount)}`
                                    : 'Set an amount to continue'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => addLimitation(benefitIndex)}
                                disabled={
                                  !resolvedLimitationLabel ||
                                  resolvedAmount === null
                                }
                              >
                                Add Limit
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setNewLimitation(getDefaultLimitationState())
                                }
                              >
                                Cancel
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setNewLimitation({
                              ...getDefaultLimitationState(),
                              benefitIndex,
                            })
                          }
                          className="border-dashed w-full"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Coverage Limit
                        </Button>
                      )}

                      {Object.keys(limitations).length === 0 &&
                        newLimitation.benefitIndex !== benefitIndex && (
                          <div className="text-center py-3 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <p className="text-sm">
                              No coverage limits specified
                            </p>
                          </div>
                        )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
